import { useState, useEffect, useCallback } from "react";
import { Users, UserPlus, Shield, Trash2, X, AlertCircle, RefreshCw, Check } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const ROLE_COLORS = {
  dj: { bg: "bg-cyan-500/15", border: "border-cyan-500/30", text: "text-cyan-400" },
  music_manager: { bg: "bg-amber-500/15", border: "border-amber-500/30", text: "text-amber-400" },
  producer: { bg: "bg-purple-500/15", border: "border-purple-500/30", text: "text-purple-400" },
  full_access: { bg: "bg-green-500/15", border: "border-green-500/30", text: "text-green-400" },
};

const PERMISSION_LABELS = {
  "view station management": "View Station",
  "view station reports": "View Reports",
  "view station logs": "View Logs",
  "manage station media": "Upload Music",
  "manage station profile": "Edit Profile",
  "manage station broadcasting": "Go Live / Stream",
  "manage station streamers": "Manage DJs",
  "manage station mounts": "Mount Points",
  "manage station remotes": "Remote Relays",
  "manage station automation": "AutoDJ Settings",
  "manage station podcasts": "Podcasts",
  "delete station media": "Delete Music",
};

export const TeamTab = ({ currentUser }) => {
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [planPerms, setPlanPerms] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("dj");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchData = useCallback(async () => {
    try {
      const [membersRes, rolesRes, permsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/roles/team`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${BACKEND_URL}/api/roles/team-roles`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${BACKEND_URL}/api/roles/permissions`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (membersRes.ok) setMembers(await membersRes.json());
      if (rolesRes.ok) setRoles(await rolesRes.json());
      if (permsRes.ok) setPlanPerms(await permsRes.json());
    } catch (err) {
      console.warn("Team data fetch error:", err.message);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setLoading(true);
    setMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/roles/team/invite`, {
        method: "POST", headers,
        body: JSON.stringify({ email: inviteEmail.trim(), name: inviteName.trim(), role: inviteRole }),
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: "success", text: data.message });
        setShowInvite(false);
        setInviteEmail("");
        setInviteName("");
        setInviteRole("dj");
        fetchData();
      } else {
        setMsg({ type: "error", text: data.detail || "Failed to invite" });
      }
    } catch {
      setMsg({ type: "error", text: "Failed to invite team member" });
    }
    setLoading(false);
    setTimeout(() => setMsg(null), 4000);
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove ${email} from your team?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/roles/team/${encodeURIComponent(email)}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMsg({ type: "success", text: "Team member removed" });
        fetchData();
      }
    } catch {
      setMsg({ type: "error", text: "Failed to remove" });
    }
    setTimeout(() => setMsg(null), 3000);
  };

  const handleSyncPerms = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/roles/sync`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: "success", text: data.message });
      } else {
        setMsg({ type: "error", text: data.detail || "Sync failed" });
      }
    } catch {
      setMsg({ type: "error", text: "Failed to sync permissions" });
    }
    setSyncing(false);
    setTimeout(() => setMsg(null), 3000);
  };

  const isStarter = currentUser?.plan === "starter" || currentUser?.plan === "listener";

  return (
    <div className="space-y-6" data-testid="team-tab">
      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm font-medium ${msg.type === "success" ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}>
          {msg.text}
        </div>
      )}

      {/* Your Plan Permissions */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="plan-permissions-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-400" />
            <h3 className="text-lg font-bold text-white">Your Station Permissions</h3>
            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-purple-500/20 text-purple-400 border border-purple-500/30 uppercase">
              {planPerms?.plan || currentUser?.plan || "starter"}
            </span>
          </div>
          <button onClick={handleSyncPerms} disabled={syncing}
            className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition disabled:opacity-40"
            data-testid="sync-perms-btn">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing..." : "Sync to AzuraCast"}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(planPerms?.permissions || []).map(p => (
            <span key={p} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 text-white/70 border border-white/10">
              {PERMISSION_LABELS[p] || p}
            </span>
          ))}
        </div>
      </div>

      {/* Team Members */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="team-members-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-400" />
            <h3 className="text-lg font-bold text-white">Team Members</h3>
            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-white/10 text-white/50">
              {members.length}
            </span>
          </div>
          {!isStarter && (
            <button onClick={() => setShowInvite(true)}
              className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-xl text-sm font-semibold transition"
              data-testid="invite-btn">
              <UserPlus className="w-4 h-4" /> Invite Member
            </button>
          )}
        </div>

        {isStarter && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 text-sm font-medium">Upgrade to Pro to invite team members</p>
              <p className="text-amber-300/60 text-xs mt-1">Pro and Premium plans allow you to add DJs, producers, and music managers to your station.</p>
            </div>
          </div>
        )}

        {members.length === 0 && !isStarter && (
          <div className="text-center py-8 text-white/30">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No team members yet. Invite your first DJ or producer!</p>
          </div>
        )}

        {members.length > 0 && (
          <div className="space-y-3">
            {members.map(m => {
              const colors = ROLE_COLORS[m.role] || ROLE_COLORS.dj;
              return (
                <div key={m.email} className="flex items-center justify-between bg-white/5 border border-white/5 rounded-xl p-4" data-testid={`team-member-${m.email}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white font-bold text-sm">
                      {(m.name || m.email)[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{m.name}</p>
                      <p className="text-white/40 text-xs">{m.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>
                      {m.role_label}
                    </span>
                    <button onClick={() => handleRemove(m.email)}
                      className="text-red-400/50 hover:text-red-400 transition p-1"
                      data-testid={`remove-${m.email}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Available Roles Reference */}
      {!isStarter && roles.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="roles-reference">
          <h3 className="text-lg font-bold text-white mb-4">Available Team Roles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {roles.map(r => {
              const colors = ROLE_COLORS[r.key] || ROLE_COLORS.dj;
              return (
                <div key={r.key} className={`${colors.bg} border ${colors.border} rounded-xl p-4`}>
                  <p className={`font-bold text-sm ${colors.text} mb-2`}>{r.label}</p>
                  <div className="flex flex-wrap gap-1">
                    {r.permissions.map(p => (
                      <span key={p} className="text-[10px] text-white/50 bg-black/20 px-2 py-0.5 rounded">
                        {PERMISSION_LABELS[p] || p}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="invite-modal-overlay">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-md w-full" data-testid="invite-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-white">Invite Team Member</h2>
              <button onClick={() => setShowInvite(false)} className="text-white/60 hover:text-white p-2 rounded-lg hover:bg-white/5">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1.5">Name</label>
                <input type="text" value={inviteName} onChange={e => setInviteName(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500 transition"
                  placeholder="DJ Name..." data-testid="invite-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1.5">Email <span className="text-red-400">*</span></label>
                <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500 transition"
                  placeholder="dj@email.com..." data-testid="invite-email-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1.5">Role</label>
                <div className="grid grid-cols-2 gap-2">
                  {roles.map(r => {
                    const colors = ROLE_COLORS[r.key] || ROLE_COLORS.dj;
                    const selected = inviteRole === r.key;
                    return (
                      <button key={r.key} onClick={() => setInviteRole(r.key)}
                        className={`p-3 rounded-xl border text-left transition ${selected ? `${colors.bg} ${colors.border} ${colors.text}` : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"}`}
                        data-testid={`role-option-${r.key}`}>
                        <p className="text-sm font-semibold">{r.label}</p>
                        <p className="text-[10px] mt-1 opacity-60">{r.permissions.length} permissions</p>
                        {selected && <Check className="w-4 h-4 mt-1" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={() => setShowInvite(false)}
                className="bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-xl transition text-sm font-medium">
                Cancel
              </button>
              <button onClick={handleInvite} disabled={loading || !inviteEmail.trim()}
                className="bg-purple-500 hover:bg-purple-600 disabled:opacity-40 text-white px-5 py-2.5 rounded-xl transition text-sm font-semibold"
                data-testid="invite-submit-btn">
                {loading ? "Inviting..." : "Send Invite"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
